/**
 * AVBP Ancillary objects — Phase 1 · Step 7.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  SOURCE OF TRUTH (owner-locked, pricing LOCKED 2026-05-16):
 *  Owner's Inbox/01_Sessions/FULLSCOPE_ANCILLARY_AVBP_v1_2026-05-16.html
 *  + LADDER_DECISIONS_LOCKED_2026-05-16.html (cross-cutting money rule).
 *
 *  Three first-class AVBP objects — each quotable, payable
 *  (money-before-delivery), schedulable, deliverable, ledger-written.
 *  Attachable to any core tier OR sold standalone. Pricing/fee-compute
 *  below is transcribed verbatim from §1.1 / §2.1 / §3.1 — NOT invented.
 *
 *    1. SiteVisit  — §1: online (= ½ offline rate) · offline local
 *                    (₹5,000/half-day, online ½ = ₹2,500) · offline
 *                    outstation (₹11,000/visit/property + travel actuals);
 *                    L1A = NO site visit at all; free-tier counter per
 *                    engagement (local L2+ = 1 pro-bono offline).
 *    2. SurveyJob  — §2: Scenario A local-survey ₹3.50/sqft min 1000
 *                    (₹3,500 min) · B client-measured → grid-only (§3) ·
 *                    C sketch draft-to-scale ₹3.00/sqft min 1000
 *                    (₹3,000 min) + grid if ordered.
 *    3. GradingJob — §3: full set ₹2,500/floor · subset 16+bar ₹1,000 ·
 *                    +Devta+Marma ₹1,500 · plot grid FREE · Astro
 *                    ₹125/chart. gradeabilityValidator → if not gradeable
 *                    auto-route to SurveyJob Scenario C (upsell).
 *
 *  PURE + dependency-free (no Firebase, no React) so the Node test runner
 *  exercises fee-compute + the validator + the free-tier counter directly.
 *  GAP-AN1/AN2 were resolved as Varun defaults in the FULLSCOPE doc
 *  ("no open items") — 32-division & Prakriti bundled into the ₹1,000
 *  base subset; no partner-channel discount applied. Encoded accordingly.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ════════════════════ Standalone vs attach-to-project ════════════════════
 *
 * OWNER-LOCKED (FULLSCOPE_ANCILLARY §"first-class AVBP object"): an
 * ancillary is NOT auto-part-of-every-project. It is either
 *   · ATTACHED to a core project  → bills THAT project's client, AND
 *   · STANDALONE                  → bills a standalone customer.
 * This is the single pure resolver the Ancillary screen + (later) the
 * payment-gate writer consult so the billing target is decided in ONE
 * place, never re-derived in the UI. No Firebase / React — Node-testable.
 * ──────────────────────────────────────────────────────────────────── */

export type AncillaryAttachMode = 'attached' | 'standalone';

/** Minimal project handle the picker offers (real list comes from CRUD). */
export interface AttachableProject {
  pid: string;
  clientName: string;
  clientNo: string;
  tier: string;
}

export interface StandaloneCustomer {
  name: string;
  phone?: string;
  email?: string;
}

/**
 * A minimal existing-client record used to recognise a standalone customer
 * who is ALREADY a client. Owner-resolved (Round 2, 2026-05-18): the
 * client number is universal per client — a standalone ancillary for an
 * existing client must resolve to their EXISTING client number (no new
 * series, no '—'). Matching is by phone or email (case/space-insensitive),
 * the stable identifiers a walk-in would give.
 */
export interface KnownClient {
  clientNo: string;
  name: string;
  phone?: string;
  email?: string;
}

export interface AncillaryBillingInput {
  mode: AncillaryAttachMode;
  /** Required when mode==='attached' — the project this attaches to. */
  project?: AttachableProject | null;
  /** Required when mode==='standalone' — who to bill directly. */
  customer?: StandaloneCustomer | null;
  /**
   * Optional existing-client directory. When a STANDALONE customer matches
   * one of these (by phone/email), billing resolves to that client's
   * EXISTING client number instead of '—'. Real source = the CRUD sprint;
   * the resolver only needs the lookup list. */
  knownClients?: KnownClient[];
}

/** Normalise a phone/email key for matching (lower, strip non-alnum). */
function normKey(v: string | undefined | null): string {
  return (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Find an existing client a standalone customer already IS. Phone first
 * (most stable for a walk-in), then email. Returns null when this is a
 * genuinely new non-client (→ light contact, no client number yet).
 */
export function matchExistingClient(
  customer: StandaloneCustomer,
  knownClients: KnownClient[] | undefined,
): KnownClient | null {
  if (!knownClients || knownClients.length === 0) return null;
  const cPhone = normKey(customer.phone);
  const cEmail = normKey(customer.email);
  for (const k of knownClients) {
    if (cPhone && normKey(k.phone) && normKey(k.phone) === cPhone) return k;
    if (cEmail && normKey(k.email) && normKey(k.email) === cEmail) return k;
  }
  return null;
}

export interface AncillaryBillingResolution {
  mode: AncillaryAttachMode;
  /** Whom the money-before-delivery gate bills. */
  billTo: { kind: 'project_client' | 'standalone_customer'; name: string };
  /** Set only when attached — the project that owns this ancillary. */
  attachedProjectId: string | null;
  /** Client number to stamp on the quote (project's, or '—' standalone). */
  clientNo: string;
  /** Plain-language summary for the staff member + the quote footer. */
  summary: string;
}

/**
 * Resolve who an ancillary bills. THROWS on an incomplete choice (a
 * picker with no project, or a standalone with no customer name) so an
 * un-billable ancillary can never reach the payment gate — the same
 * "never reaches the data layer" discipline as transitionLead.
 */
export function resolveAncillaryBilling(
  inp: AncillaryBillingInput,
): AncillaryBillingResolution {
  if (inp.mode === 'attached') {
    const p = inp.project;
    if (!p || !p.pid) {
      throw new Error(
        'Attached ancillary needs a project — pick one in the project picker.',
      );
    }
    return {
      mode: 'attached',
      billTo: { kind: 'project_client', name: p.clientName },
      attachedProjectId: p.pid,
      clientNo: p.clientNo,
      summary: `Attached to ${p.pid} (${p.tier}) · bills the project's client ${p.clientName} (${p.clientNo}).`,
    };
  }
  const c = inp.customer;
  const name = c?.name?.trim();
  if (!name) {
    throw new Error(
      'Standalone ancillary needs a customer name — it bills a standalone customer.',
    );
  }
  // Round 2 (owner-resolved): a standalone customer who is ALREADY a
  // client resolves to their EXISTING client number — universal per
  // client, no new series, never '—'. A genuinely new non-client stays a
  // light contact with no client number ('—') until they convert.
  const existing = matchExistingClient(c as StandaloneCustomer, inp.knownClients);
  if (existing) {
    return {
      mode: 'standalone',
      billTo: { kind: 'standalone_customer', name: existing.name || name },
      attachedProjectId: null,
      clientNo: existing.clientNo,
      summary: `Standalone · ${existing.name || name} is an EXISTING client (${existing.clientNo}) — bills their existing client number. Not part of any project.`,
    };
  }
  return {
    mode: 'standalone',
    billTo: { kind: 'standalone_customer', name },
    attachedProjectId: null,
    clientNo: '—',
    summary: `Standalone · bills standalone customer ${name}${
      c?.phone ? ` (${c.phone})` : ''
    }. New non-client — no client number until they convert. Not part of any project.`,
  };
}

/* ════════════════════ Shared ════════════════════ */

export type AncillaryKind = 'site_visit' | 'survey' | 'grading';

export interface FeeLine {
  label: string;
  amountINR: number;
}

export interface FeeQuote {
  lines: FeeLine[];
  /** Excludes travel actuals (outstation), which are reimbursed at cost. */
  subtotalINR: number;
  /** True when travel actuals (lodging/boarding/transport) are extra. */
  travelActualsExtra: boolean;
  notes: string[];
}

const sum = (lines: FeeLine[]) => lines.reduce((s, l) => s + l.amountINR, 0);

/* ════════════════════ 1 · SiteVisit (§1) ════════════════════ */

export type VisitMode = 'online' | 'offline';
export type VisitLocation = 'local' | 'outstation';

/** §1.1 LOCKED base rates. */
export const SITE_VISIT_OUTSTATION_INR = 11000; // /visit/property + travel
export const SITE_VISIT_LOCAL_OFFLINE_INR = 5000; // /visit/half-day
// Online = ½ of the applicable offline rate (§1.1, "corrected").

export interface SiteVisitInput {
  mode: VisitMode;
  location: VisitLocation;
  /** Core tier this attaches to, or null = standalone. L1A → no visit. */
  attachedTier: string | null;
  /** Properties covered in one outstation trip (§1.1 multi-property). */
  propertyCount?: number;
  /**
   * Free-tier allowance ALREADY used on the attached engagement. The free
   * allowance is 1 pro-bono OFFLINE visit for local L2-and-above (§1.1).
   */
  freeOfflineUsed?: number;
}

export interface SiteVisitFee extends FeeQuote {
  /** True when this visit is consumed from the free-tier allowance. */
  coveredByFreeTier: boolean;
  /** L1A blocks site visits entirely (§1.1 LOCKED). */
  blocked: boolean;
}

/** Is the engagement local L2-and-above (carries 1 free offline visit)? */
export function hasFreeOfflineAllowance(attachedTier: string | null): boolean {
  if (!attachedTier) return false;
  const m = attachedTier.toUpperCase().match(/L([123])/);
  return !!m && Number(m[1]) >= 2;
}

export function quoteSiteVisit(inp: SiteVisitInput): SiteVisitFee {
  const notes: string[] = [];
  // §1.1 LOCKED — L1A: no site visit at all (online or offline).
  if (inp.attachedTier && /L1A/i.test(inp.attachedTier)) {
    return {
      lines: [],
      subtotalINR: 0,
      travelActualsExtra: false,
      notes: ['L1A carries no site visit (online or offline) — FULLSCOPE §1.1.'],
      coveredByFreeTier: false,
      blocked: true,
    };
  }

  // Free-tier: 1 pro-bono OFFLINE visit for local L2+ engagements.
  const freeUsed = inp.freeOfflineUsed ?? 0;
  const eligibleFree =
    inp.mode === 'offline' &&
    inp.location === 'local' &&
    hasFreeOfflineAllowance(inp.attachedTier) &&
    freeUsed < 1;
  if (eligibleFree) {
    return {
      lines: [{ label: 'Local offline site visit (free-tier — pro-bono)', amountINR: 0 }],
      subtotalINR: 0,
      travelActualsExtra: false,
      notes: ['Consumed from the engagement free-tier allowance (1 local offline visit, L2+).'],
      coveredByFreeTier: true,
      blocked: false,
    };
  }

  const lines: FeeLine[] = [];
  let travelActualsExtra = false;
  const props = Math.max(1, inp.propertyCount ?? 1);

  if (inp.location === 'outstation') {
    const offline = SITE_VISIT_OUTSTATION_INR * props;
    const rate = inp.mode === 'online' ? offline / 2 : offline;
    lines.push({
      label:
        inp.mode === 'online'
          ? `Outstation visit — online (½ of ₹${SITE_VISIT_OUTSTATION_INR}/property × ${props})`
          : `Outstation visit — offline (₹${SITE_VISIT_OUTSTATION_INR}/property × ${props})`,
      amountINR: rate,
    });
    if (inp.mode === 'offline') {
      travelActualsExtra = true;
      notes.push(
        'Plus to-fro travel + lodging/boarding at actuals (§1.1). Multi-property: shared travel apportioned.',
      );
    }
  } else {
    // local, beyond free tier
    const offline = SITE_VISIT_LOCAL_OFFLINE_INR;
    const rate = inp.mode === 'online' ? offline / 2 : offline;
    lines.push({
      label:
        inp.mode === 'online'
          ? `Local visit — online (½ × ₹${SITE_VISIT_LOCAL_OFFLINE_INR}/half-day)`
          : `Local visit — offline (₹${SITE_VISIT_LOCAL_OFFLINE_INR}/half-day, beyond free tier)`,
      amountINR: rate,
    });
  }

  return {
    lines,
    subtotalINR: sum(lines),
    travelActualsExtra,
    notes,
    coveredByFreeTier: false,
    blocked: false,
  };
}

/* ════════════════════ 2 · SurveyJob (§2) ════════════════════ */

export type SurveyScenario = 'A' | 'B' | 'C';

/** §2.1 LOCKED rates. */
export const SURVEY_A_RATE = 3.5; // /sqft, survey + map making
export const SURVEY_C_RATE = 3.0; // /sqft, draft-to-scale from sketch
export const SURVEY_MIN_SQFT = 1000; // minimum basis (₹3,500 / ₹3,000 min)

export interface SurveyInput {
  scenario: SurveyScenario;
  areaSqft: number;
  /** Scenario B/C: floors for the grid-making add-on (§3 rate). */
  gridFloors?: number;
}

export function quoteSurvey(inp: SurveyInput): FeeQuote {
  const lines: FeeLine[] = [];
  const notes: string[] = [];
  const billSqft = Math.max(inp.areaSqft, SURVEY_MIN_SQFT);

  if (inp.scenario === 'A') {
    lines.push({
      label: `Survey + map making · ₹3.50/sqft × ${billSqft} sqft (min ${SURVEY_MIN_SQFT})`,
      amountINR: Math.round(billSqft * SURVEY_A_RATE),
    });
    notes.push('Outstation site visits add Site-Visit charges per §1.');
  } else if (inp.scenario === 'C') {
    lines.push({
      label: `Draft-to-scale from sketch · ₹3.00/sqft × ${billSqft} sqft (min ${SURVEY_MIN_SQFT})`,
      amountINR: Math.round(billSqft * SURVEY_C_RATE),
    });
  } else {
    // Scenario B — client supplied a usable measured drawing. AV does NOT
    // survey/draft. Only grid-making is charged (per §3).
    notes.push(
      'Scenario B — no survey/draft fee. Only grid-making is charged (§3 Map Grading).',
    );
  }

  if ((inp.scenario === 'B' || inp.scenario === 'C') && inp.gridFloors) {
    const grid = quoteGrading({ floors: inp.gridFloors, overlay: 'full' });
    lines.push({
      label: `Grid-making add-on (§3, full set) × ${inp.gridFloors} floor(s)`,
      amountINR: grid.subtotalINR,
    });
  }

  return {
    lines,
    subtotalINR: sum(lines),
    travelActualsExtra: false,
    notes,
  };
}

/* ════════════════════ 3 · GradingJob (§3) ════════════════════ */

export type GradingOverlay = 'full' | 'subset_16_bar';

/** §3.1 LOCKED rates. */
export const GRADING_FULL_PER_FLOOR = 2500; // full grid set / floor
export const GRADING_SUBSET_PER_FLOOR = 1000; // 16-div + bar chart / floor
export const GRADING_DEVTA_MARMA_ADDON = 1500; // +Devta+Marma / floor (1000+1500=2500)
export const ASTRO_PER_CHART = 125; // chart prep only
// Plot grid = FREE/pro-bono (included when floor grids are made).

export interface GradingInput {
  floors: number;
  overlay: GradingOverlay;
  /** subset_16_bar + this → adds the +₹1,500/floor Devta+Marma add-on. */
  addDevtaMarma?: boolean;
  /** Astro charts ordered (₹125/chart, chart prep only). */
  astroCharts?: number;
}

export function quoteGrading(inp: GradingInput): FeeQuote {
  const lines: FeeLine[] = [];
  const floors = Math.max(1, inp.floors);

  if (inp.overlay === 'full') {
    lines.push({
      label: `Full grid set · ₹2,500/floor × ${floors}`,
      amountINR: GRADING_FULL_PER_FLOOR * floors,
    });
  } else {
    lines.push({
      label: `16-division + bar chart · ₹1,000/floor × ${floors}`,
      amountINR: GRADING_SUBSET_PER_FLOOR * floors,
    });
    if (inp.addDevtaMarma) {
      lines.push({
        label: `+ Devta + Marma · ₹1,500/floor × ${floors}`,
        amountINR: GRADING_DEVTA_MARMA_ADDON * floors,
      });
    }
  }
  lines.push({ label: 'Plot grid (free / pro-bono — included)', amountINR: 0 });

  if (inp.astroCharts && inp.astroCharts > 0) {
    lines.push({
      label: `Astro charts · ₹125/chart × ${inp.astroCharts}`,
      amountINR: ASTRO_PER_CHART * inp.astroCharts,
    });
  }

  return {
    lines,
    subtotalINR: sum(lines),
    travelActualsExtra: false,
    notes: [
      '32-division & Prakriti map bundled into the ₹1,000 base subset (GAP-AN1 — Varun default). No partner-channel discount (GAP-AN2 — Varun default).',
    ],
  };
}

/* ──────────── §3.3 gradeability validator → Scenario-C upsell ──────────── */

export interface GradeabilityInput {
  /** Map carries a stated scale. */
  hasScale: boolean;
  /** North is marked or its degree is known. */
  hasNorth: boolean;
  /** The drawing is dimensioned. */
  isDimensioned: boolean;
}

export interface GradeabilityResult {
  gradeable: boolean;
  /** When not gradeable → the FULLSCOPE-locked auto-route target. */
  routeTo: 'grading' | 'survey_scenario_c';
  missing: string[];
  message: string;
}

/**
 * §3.3: "gradeability validator checks scale + north + dimensions. If not
 * gradeable → auto-route to Survey & Map Making (Scenario C upsell) or
 * request corrected map." All three must hold to grade directly.
 */
export function validateGradeability(
  inp: GradeabilityInput,
): GradeabilityResult {
  const missing: string[] = [];
  if (!inp.hasScale) missing.push('stated scale');
  if (!inp.hasNorth) missing.push('north reference');
  if (!inp.isDimensioned) missing.push('dimensions');
  if (missing.length === 0) {
    return {
      gradeable: true,
      routeTo: 'grading',
      missing,
      message: 'Map is gradeable — proceed to grid making.',
    };
  }
  return {
    gradeable: false,
    routeTo: 'survey_scenario_c',
    missing,
    message: `Map is not gradeable (missing: ${missing.join(
      ', ',
    )}). Auto-routing to Survey & Map Making — Scenario C (draft-to-scale, ₹3.00/sqft).`,
  };
}

/* ──────────── Free-tier counter (per-engagement, §1.1) ──────────── */

/**
 * Per-engagement free-tier ledger for site visits. Local L2+ engagements
 * carry exactly 1 pro-bono OFFLINE visit (§1.1). This is the pure counter
 * the SiteVisit object's `freeTierCounter` reads/writes; persistence is a
 * later sprint (it lives on the engagement doc), the rule lives here.
 */
export interface FreeTierState {
  freeOfflineUsed: number;
}

export function freeOfflineRemaining(
  attachedTier: string | null,
  state: FreeTierState,
): number {
  if (!hasFreeOfflineAllowance(attachedTier)) return 0;
  return Math.max(0, 1 - state.freeOfflineUsed);
}

export function consumeFreeOffline(state: FreeTierState): FreeTierState {
  return { freeOfflineUsed: state.freeOfflineUsed + 1 };
}
